import { useState, useRef } from 'react';
import useAppStore from '../../app-store';
import { useNavigate } from 'react-router-dom';
import styles from './index.module.scss';
import { Swiper, SwiperSlide } from 'swiper/react';
import isNaN from 'lodash/fp/isNaN';
import { PromotionCarouselPrevButton } from './promotion-carousel-prev-button';
import { PromotionCarouselNextButton } from './promotion-carousel-next-button';
import { PromotionCarouselIndicator } from './promotion-carousel-indicator';
import {
  DEVICE_PLATFORM,
  generateModulePath,
  useDeviceType,
} from '@autopass-web/domain';
import { analytics } from '../../app-loader';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';

type Promotion = {
  description: string;
  endAt: string;
  id: string;
  imageUrl: string;
  notEndPromotionIds: string[];
  position: number;
  promotionTypes: string[];
  startAt: string;
  title: string;
  attributes?: {
    url: string;
    imageUrl: string;
  };
};

interface PromotionCarouselProps {
  promotions: Promotion[];
}

export const PromotionCarousel = ({ promotions }: PromotionCarouselProps) => {
  const startPosition = useRef<number | null>(null);

  const appStore = useAppStore();
  const [swiper, setSwiper] = useState<SwiperType | null>(null);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isDraging, setIsDraging] = useState<boolean>(false);
  const [itemMovingRatio, setItemMovingRatio] = useState<number>(0);
  const device = useDeviceType();
  const navigate = useNavigate();
  const generatePath = generateModulePath(
    appStore.nextModule,
    appStore.basePath,
  );

  if (promotions.length === 0) return null;

  const handleSlideChange = (swiper: SwiperType) => {
    const { activeIndex } = swiper;
    setCurrentIndex(activeIndex);
  };

  const handleMoveTo = (index: number) => {
    swiper?.slideTo(index);
  };

  const swiperBreakPoints = {
    320: {
      slidesPerView: 1.2,
      spaceBetween: 8,
    },
    768: {
      slidesPerView: 1.5,
      spaceBetween: 8,
    },
    1080: {
      slidesPerView: 1.8,
      spaceBetween: 8,
    },
    1200: {
      slidesPerView: 2.2,
      spaceBetween: 8,
    },
    1440: {
      slidesPerView: 2.5,
      spaceBetween: 8,
    },
    1520: {
      slidesPerView: 2.6,
      spaceBetween: 8,
    },
    1800: {
      slidesPerView: 3,
      spaceBetween: 8,
    },
    2110: {
      slidesPerView: 3,
      spaceBetween: 8,
    },
  };

  const isFirstItem = currentIndex === 0;
  const isLastItem = currentIndex === promotions.length - 1;
  const isDesktop = device === DEVICE_PLATFORM.DESKTOP;

  const isPrevButtonShow = !isFirstItem && isDesktop;
  const isNextButtonShow = !isLastItem && isDesktop;

  return (
    <div className={styles['container']}>
      <div className={styles['swiper-container']}>
        {isPrevButtonShow && (
          <PromotionCarouselPrevButton onClick={() => swiper?.slidePrev?.()} />
        )}
        <Swiper
          onSwiper={setSwiper}
          onSlideChange={handleSlideChange}
          breakpoints={swiperBreakPoints}
          centeredSlides
          height={300}
          draggable
          onSliderFirstMove={(swiper) => {
            startPosition.current = swiper.translate;
            setIsDraging(true);
          }}
          onSlideChangeTransitionStart={() => {
            startPosition.current = null;
            setIsDraging(false);
          }}
          onSetTranslate={(currentSwiper, currentPosition) => {
            const diff = currentPosition - (startPosition.current ?? 0);
            const singleItemWidth = Math.round(
              currentSwiper.snapGrid[1] - currentSwiper.snapGrid[0] ?? 0,
            );

            if (isNaN(diff) || currentSwiper == null) return;

            // If diff is positive, it means the user is dragging to the right
            // If diff is negative, it means the user is dragging to the left
            const ratio = -(diff / singleItemWidth);

            setItemMovingRatio(ratio);
          }}
        >
          {promotions.map((promotion) => {
            const isFromExternal = !!promotion.attributes;
            const url = isFromExternal
              ? (promotion.attributes?.url as string)
              : (generatePath(`/group/${promotion.id}`) as string);

            const imageUrl = isFromExternal
              ? promotion.attributes?.imageUrl
              : promotion.imageUrl;

            return (
              <SwiperSlide
                key={promotion.id}
                onClick={() => {
                  if (isFromExternal) window.location.href = url;
                  else navigate(generatePath(`${url}`));

                  analytics.promotion_banner_onClick({
                    promotion_collection_id: promotion.id,
                    promotion_collection_name: promotion.title,
                    url,
                    board_name: null,
                  });
                }}
              >
                <img
                  className={styles['swiper-image']}
                  src={imageUrl}
                  alt={promotion.title}
                />
              </SwiperSlide>
            );
          })}
        </Swiper>
        {isNextButtonShow && (
          <PromotionCarouselNextButton onClick={() => swiper?.slideNext?.()} />
        )}
      </div>
      <PromotionCarouselIndicator
        total={promotions.length}
        index={currentIndex}
        isDraging={isDraging}
        ratio={itemMovingRatio}
        onMoveTo={handleMoveTo}
      />
    </div>
  );
};
